<template>
    <vx-modal v-model:visible="visible" size="lg" padding="standard">
        <template v-slot:header> Terms Acceptances </template>
        <template v-slot:subheader>
            {{ props.term?.title }}
        </template>

        <vx-tabs v-model="tab" size="lg" class="mb-6">
            <vx-tab value="accepted" data-test="accepted-tab">Accepted</vx-tab>
            <vx-tab value="not_accepted" data-test="not-accepted-tab">Not Accepted</vx-tab>
        </vx-tabs>

        <div v-show="tab === 'accepted'" data-test="accepted-panel">
            <page-loading v-if="acceptedLoading && !acceptedList.length" />

            <vx-box
                v-else-if="!acceptedList.length"
                color="info"
                class="px-6 py-12 text-center text-slate-500"
                data-test="accepted-empty"
            >
                No groups have accepted these Terms yet.
            </vx-box>

            <div v-else class="flex flex-col gap-4">
                <vx-box
                    v-for="acceptance in acceptedList"
                    :key="acceptance.id"
                    data-test="accepted-row"
                    color="white"
                    class="p-4 text-slate-800"
                >
                    <div class="flex flex-col gap-1 items-start justify-between sm:flex-row sm:items-center">
                        <div class="flex flex-col gap-1">
                            <div class="text-md font-bold wrap-break-word">
                                {{ acceptance.group_name || acceptance.group_id }}
                            </div>
                            <span v-if="acceptance.user?.name" class="text-sm text-slate-500">{{
                                acceptance.user.name
                            }}</span>
                            <span class="text-sm text-slate-400 wrap-break-word">{{ acceptance.user_email }}</span>
                        </div>
                        <span class="text-sm text-slate-500 shrink-0">{{
                            toShortDateTime(acceptance.accepted_at)
                        }}</span>
                    </div>
                </vx-box>

                <div v-if="acceptedList.length && !acceptedIsLast" class="pt-2">
                    <vx-button
                        @click="loadAccepted()"
                        color="muted-dark"
                        block
                        :loading="acceptedLoading"
                        data-test="accepted-load-more"
                    >
                        Load More <font-awesome-icon widthAuto :icon="faSpinner" />
                    </vx-button>
                </div>
            </div>
        </div>

        <div v-show="tab === 'not_accepted'" data-test="not-accepted-panel">
            <page-loading v-if="notAcceptedLoading && !notAcceptedList.length" />

            <vx-box
                v-else-if="!notAcceptedList.length"
                color="info"
                class="px-6 py-12 text-center text-slate-500"
                data-test="not-accepted-empty"
            >
                Every group has accepted these Terms.
            </vx-box>

            <div v-else class="flex flex-col gap-4">
                <vx-box
                    v-for="group in notAcceptedList"
                    :key="group.group_id"
                    data-test="not-accepted-row"
                    color="white"
                    class="p-4 text-slate-800"
                >
                    <div class="flex flex-col gap-1 items-start justify-between sm:flex-row sm:items-center">
                        <div class="flex flex-col gap-1">
                            <div class="text-md font-bold wrap-break-word">{{ group.name || group.group_id }}</div>
                            <span class="text-sm text-slate-500">
                                <template v-if="group.owner">
                                    Owner: {{ group.owner.name }}
                                    <span class="text-slate-400">&lt;{{ group.owner.email }}&gt;</span>
                                </template>
                                <template v-else>No owner</template>
                            </span>
                        </div>
                        <group-status-badge :status="group.status" class="shrink-0" />
                    </div>
                </vx-box>

                <div v-if="notAcceptedList.length && !notAcceptedIsLast" class="pt-2">
                    <vx-button
                        @click="loadNotAccepted()"
                        color="muted-dark"
                        block
                        :loading="notAcceptedLoading"
                        data-test="not-accepted-load-more"
                    >
                        Load More <font-awesome-icon widthAuto :icon="faSpinner" />
                    </vx-button>
                </div>
            </div>
        </div>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxBox, VxButton, VxModal, VxTab, VxTabs } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';
import { toShortDateTime } from '~/utils/date';
import { adminTermsClient } from '~/services';
import type { PendingAcceptanceGroup, Term, TermAcceptance } from '~/services/adminTermsClient.types';
import { useCursorPagination } from '~/composables/useCursorPagination';

const PER_PAGE = 20;

const props = defineProps<{
    teamId: number;
    term: Term | null;
}>();

const visible = defineModel<boolean>('visible', { default: false });

const tab = ref<'accepted' | 'not_accepted'>('accepted');
const notAcceptedFetched = ref(false);

const {
    list: acceptedList,
    loading: acceptedLoading,
    fetchParams: acceptedParams,
    fetchRecords: fetchAccepted,
    isLast: acceptedIsLast,
} = useCursorPagination<TermAcceptance>();

const {
    list: notAcceptedList,
    loading: notAcceptedLoading,
    fetchParams: notAcceptedParams,
    fetchRecords: fetchNotAccepted,
    isLast: notAcceptedIsLast,
} = useCursorPagination<PendingAcceptanceGroup>();

const loadAccepted = () => {
    if (!props.term) {
        return;
    }
    return fetchAccepted(adminTermsClient.getTermAcceptances(props.teamId, props.term.id, acceptedParams.value));
};

const loadNotAccepted = () => {
    if (!props.term) {
        return;
    }
    return fetchNotAccepted(
        adminTermsClient.getPendingAcceptanceGroups(props.teamId, props.term.id, notAcceptedParams.value)
    );
};

const reloadAccepted = () => {
    acceptedParams.value = { per_page: PER_PAGE, cursor: null };
    acceptedList.value = [];
    return loadAccepted();
};

const reloadNotAccepted = () => {
    notAcceptedParams.value = { per_page: PER_PAGE, cursor: null };
    notAcceptedList.value = [];
    notAcceptedFetched.value = true;
    return loadNotAccepted();
};

watch(tab, (value) => {
    if (value === 'not_accepted' && !notAcceptedFetched.value) {
        reloadNotAccepted();
    }
});

watch(
    () => [visible.value, props.term?.id],
    () => {
        if (!visible.value || !props.term) {
            return;
        }
        tab.value = 'accepted';
        notAcceptedFetched.value = false;
        notAcceptedList.value = [];
        reloadAccepted();
    }
);
</script>
